/**
 * Admin Moderation Dashboard
 * Unified content moderation system
 * Solis Games - Phase 2.1 Implementation
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { moderationService } from '../../utils/moderationService';
import ModerationDetailModal from './components/ModerationDetailModal';

export default function AdminModerationDashboard() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [queueItems, setQueueItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({
    status: 'pending',
    severity: null,
    contentType: null,
    gameId: null
  });
  const [stats, setStats] = useState({
    pendingCount: 0,
    criticalCount: 0,
    inReviewCount: 0,
    todayResolved: 0,
    avgResponseTime: 0
  });

  // Check admin access
  useEffect(() => {
    if (!user || userProfile?.role !== 'admin') {
      navigate('/');
      return;
    }

    loadModerationQueue();
    loadStats();

    // Subscribe to real-time updates
    const subscription = moderationService.subscribeToQueueUpdates(handleRealtimeUpdate);

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [user, userProfile, navigate, filters]);

  const loadModerationQueue = async () => {
    setLoading(true);
    const { data, error } = await moderationService.getModerationQueue(filters);

    if (error) {
      console.error('Error loading moderation queue:', error);
    } else {
      setQueueItems(data || []);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    const { data, error } = await moderationService.getModerationStats();

    if (error) {
      console.error('Error loading stats:', error);
    } else if (data) {
      setStats(data);
    }
  };

  const handleRealtimeUpdate = (payload) => {
    console.log('Realtime update:', payload);
    loadModerationQueue();
    loadStats();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleRefresh = () => {
    loadModerationQueue();
    loadStats();
  };

  const handleActionComplete = () => {
    loadModerationQueue();
    loadStats();
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'high': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'low': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-500';
      case 'in_review': return 'bg-blue-500/10 text-blue-500';
      case 'approved': return 'bg-green-500/10 text-green-500';
      case 'rejected': return 'bg-red-500/10 text-red-500';
      case 'escalated': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />

      <div className="lg:pl-72">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground flex items-center gap-3">
                  <Icon name="Shield" size={32} className="text-primary" />
                  Moderation Dashboard
                </h1>
                <p className="text-muted-foreground mt-1">
                  Unified content moderation across all games
                </p>
              </div>

              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-4 pb-20 lg:pb-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Clock" size={20} className="text-yellow-500" />
                <span className="text-xs text-muted-foreground">Pending</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.pendingCount}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="AlertTriangle" size={20} className="text-red-500" />
                <span className="text-xs text-muted-foreground">Critical</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.criticalCount}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Eye" size={20} className="text-blue-500" />
                <span className="text-xs text-muted-foreground">In Review</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.inReviewCount}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="CheckCircle" size={20} className="text-green-500" />
                <span className="text-xs text-muted-foreground">Today</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.todayResolved}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Timer" size={20} className="text-purple-500" />
                <span className="text-xs text-muted-foreground">Avg Time</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.avgResponseTime}m</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select
                  value={filters.status || 'all'}
                  onChange={(e) => handleFilterChange('status', e.target.value === 'all' ? null : e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="in_review">In Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="escalated">Escalated</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Severity</label>
                <select
                  value={filters.severity || 'all'}
                  onChange={(e) => handleFilterChange('severity', e.target.value === 'all' ? null : e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Content Type</label>
                <select
                  value={filters.contentType || 'all'}
                  onChange={(e) => handleFilterChange('contentType', e.target.value === 'all' ? null : e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All</option>
                  <option value="chat_message">Chat Messages</option>
                  <option value="leaderboard_score">Leaderboard Scores</option>
                  <option value="user_report">User Reports</option>
                </select>
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setFilters({ status: 'pending', severity: null, contentType: null, gameId: null })}
                >
                  Reset Filters
                </Button>
              </div>
            </div>
          </div>

          {/* Queue Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Content</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">User</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Game</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Severity</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-foreground">Flagged</th>
                    <th className="text-right p-4 text-sm font-medium text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center p-8">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-muted-foreground">Loading moderation queue...</p>
                      </td>
                    </tr>
                  ) : queueItems.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center p-8">
                        <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-2" />
                        <p className="text-muted-foreground">No items in moderation queue</p>
                      </td>
                    </tr>
                  ) : (
                    queueItems.map((item) => (
                      <tr key={item.id} className="border-t border-border hover:bg-muted/50">
                        <td className="p-4">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-500/10 text-gray-500">
                            {item.content_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="p-4">
                          <p className="text-sm text-foreground line-clamp-2">
                            {item.content_snapshot?.message || item.flag_reason}
                          </p>
                          {item.auto_flagged && (
                            <span className="text-xs text-purple-500 flex items-center gap-1 mt-1">
                              <Icon name="Zap" size={12} />
                              Auto-flagged ({(item.confidence_score * 100).toFixed(0)}%)
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <p className="text-sm font-medium text-foreground">{item.reported_user_name}</p>
                          <p className="text-xs text-muted-foreground">{item.user_violation_count} violations</p>
                        </td>
                        <td className="p-4 text-sm text-foreground">{item.game_title || 'N/A'}</td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(item.severity)}`}>
                            {item.severity}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="p-4 text-xs text-muted-foreground">
                          {new Date(item.flagged_at).toLocaleString()}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            onClick={() => setSelectedItem(item)}
                          >
                            Review
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <ModerationDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onActionComplete={handleActionComplete}
        />
      )}
    </div>
  );
}
